import { useEffect, useState } from "react";
import { ArrowLeft, Upload, X } from "lucide-react";
import { adminFetch } from "../../lib/adminApi";
import { mediaUrl } from "../../lib/media";

const EMPTY = {
  slug: "", title: "", client: "", show: "", location: "", year: "",
  hook: "", summary: "", sample: false, published: true,
  poster: "", video: "", videoMp4: "", gallery: [],
};

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const inputCls =
  "w-full border border-white/15 bg-ink px-4 py-3 text-sm text-paper placeholder:text-paper/30 transition-colors focus:border-acid focus:outline-none";
const labelCls = "mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-paper/50";

const Toggle = ({ label, value, onChange, testid }) => (
  <button
    type="button"
    role="switch"
    aria-checked={value}
    data-testid={testid}
    onClick={() => onChange(!value)}
    className={`flex items-center gap-3 border px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
      value ? "border-acid bg-acid/10 text-acid" : "border-white/15 text-paper/50 hover:border-white/40"
    }`}
  >
    <span className={`h-2 w-2 rounded-full ${value ? "bg-acid" : "bg-paper/30"}`} aria-hidden="true" />
    {label}
  </button>
);

export default function ProjectEditor({ projectId, onBack }) {
  const [form, setForm] = useState(EMPTY);
  const [scopeText, setScopeText] = useState("");
  const [builtText, setBuiltText] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [uploading, setUploading] = useState("");
  const [loaded, setLoaded] = useState(!projectId);

  useEffect(() => {
    if (!projectId) return;
    adminFetch("/api/admin/projects")
      .then((r) => r.json())
      .then((list) => {
        const p = Array.isArray(list) ? list.find((x) => x.id === projectId) : null;
        if (p) {
          setForm({ ...EMPTY, ...p });
          setScopeText((p.scope || []).join(", "));
          setBuiltText((p.built || []).join("\n"));
          setSlugTouched(true);
        }
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [projectId]);

  const set = (k) => (e) => {
    const v = e.target.value;
    setForm((f) => ({
      ...f,
      [k]: v,
      ...(k === "title" && !slugTouched ? { slug: slugify(v) } : {}),
    }));
  };

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await adminFetch("/api/admin/upload", { method: "POST", body: fd });
    if (!res.ok) throw new Error("Upload failed");
    return (await res.json()).path;
  };

  const onPick = (kind) => async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(kind);
    setStatus("");
    try {
      for (const file of files) {
        const path = await upload(file);
        setForm((f) =>
          kind === "gallery"
            ? { ...f, gallery: [...f.gallery, { src: path, alt: file.name }] }
            : { ...f, [kind]: path }
        );
      }
    } catch {
      setStatus("Upload failed — check the file type and size (200MB max) and try again");
    }
    setUploading("");
  };

  const save = async () => {
    setSaving(true);
    setStatus("");
    const payload = {
      ...form,
      scope: scopeText.split(",").map((s) => s.trim()).filter(Boolean),
      built: builtText.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    const res = await adminFetch(
      projectId ? `/api/admin/projects/${projectId}` : "/api/admin/projects",
      { method: projectId ? "PUT" : "POST", body: JSON.stringify(payload) }
    );
    setSaving(false);
    if (res.ok) {
      onBack();
      return;
    }
    const data = await res.json().catch(() => ({}));
    setStatus(typeof data.detail === "string" ? data.detail : "Save failed — check the fields");
  };

  if (!loaded) {
    return (
      <div className="flex min-h-svh items-center justify-center bg-ink">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-paper/50">Loading…</p>
      </div>
    );
  }

  return (
    <div data-testid="project-editor" className="min-h-svh bg-ink text-paper">
      <header className="border-b border-white/10">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-5 sm:px-6">
          <button
            type="button"
            data-testid="editor-back-button"
            onClick={onBack}
            className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-paper/60 transition-colors hover:text-acid"
          >
            <ArrowLeft className="h-4 w-4" /> All Projects
          </button>
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-paper/50">
            {projectId ? "Edit Project" : "New Project"}
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label htmlFor="ed-title" className={labelCls}>Project Title *</label>
            <input id="ed-title" data-testid="editor-title-input" value={form.title} onChange={set("title")} className={inputCls} placeholder="e.g. Hall of Flowers Flagship Booth" />
          </div>
          <div>
            <label htmlFor="ed-slug" className={labelCls}>URL Slug *</label>
            <input id="ed-slug" data-testid="editor-slug-input" value={form.slug} onChange={(e) => { setSlugTouched(true); set("slug")(e); }} className={inputCls} placeholder="hall-of-flowers-booth" />
            <p className="mt-2 font-mono text-[10px] text-paper/40">/work/{form.slug || "…"}</p>
          </div>
          <div>
            <label htmlFor="ed-year" className={labelCls}>Year</label>
            <input id="ed-year" data-testid="editor-year-input" value={form.year} onChange={set("year")} className={inputCls} placeholder="2026" />
          </div>
          <div>
            <label htmlFor="ed-client" className={labelCls}>Client</label>
            <input id="ed-client" data-testid="editor-client-input" value={form.client} onChange={set("client")} className={inputCls} placeholder="Brand name (or leave blank)" />
          </div>
          <div>
            <label htmlFor="ed-show" className={labelCls}>Show / Event</label>
            <input id="ed-show" data-testid="editor-show-input" value={form.show} onChange={set("show")} className={inputCls} placeholder="e.g. Hall of Flowers" />
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="ed-location" className={labelCls}>Location / Venue</label>
            <input id="ed-location" data-testid="editor-location-input" value={form.location} onChange={set("location")} className={inputCls} placeholder="e.g. Anaheim Convention Center" />
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="ed-hook" className={labelCls}>One-Line Hook</label>
            <input id="ed-hook" data-testid="editor-hook-input" value={form.hook} onChange={set("hook")} className={inputCls} placeholder="A show-floor flagship built to stop traffic." />
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="ed-summary" className={labelCls}>The Story</label>
            <textarea id="ed-summary" data-testid="editor-summary-input" rows={6} value={form.summary} onChange={set("summary")} className={`${inputCls} resize-y`} placeholder="The goal, the constraints, the idea, the build…" />
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="ed-scope" className={labelCls}>Scope Tags (comma separated)</label>
            <input id="ed-scope" data-testid="editor-scope-input" value={scopeText} onChange={(e) => setScopeText(e.target.value)} className={inputCls} placeholder="20×30 Island Booth, Product Displays, Lighting" />
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="ed-built" className={labelCls}>What We Built (one per line)</label>
            <textarea id="ed-built" data-testid="editor-built-input" rows={5} value={builtText} onChange={(e) => setBuiltText(e.target.value)} className={`${inputCls} resize-y`} placeholder={"Custom booth structure\nIlluminated display cases\nLarge-format graphics"} />
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Toggle label="Published (visible on site)" value={form.published} onChange={(v) => setForm((f) => ({ ...f, published: v }))} testid="editor-published-toggle" />
          <Toggle label="Sample badge" value={form.sample} onChange={(v) => setForm((f) => ({ ...f, sample: v }))} testid="editor-sample-toggle" />
        </div>

        <div className="mt-10 space-y-8 border-t border-white/10 pt-8">
          <div>
            <span className={labelCls}>Cover Image (poster)</span>
            {form.poster && (
              <img src={mediaUrl(form.poster)} alt="Cover" className="mb-3 aspect-video w-full max-w-md border border-white/10 object-cover" />
            )}
            <label data-testid="editor-poster-upload" className="flex w-fit cursor-pointer items-center gap-2 border border-white/15 px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-paper/70 transition-colors hover:border-acid hover:text-acid">
              <Upload className="h-3.5 w-3.5" />
              {uploading === "poster" ? "Uploading…" : form.poster ? "Replace Image" : "Upload Image"}
              <input type="file" accept="image/*" className="hidden" onChange={onPick("poster")} />
            </label>
          </div>

          <div>
            <span className={labelCls}>Hero Video (optional)</span>
            {form.video && (
              <p className="mb-3 break-all font-mono text-[10px] uppercase tracking-[0.15em] text-acid">
                Video attached ✓
              </p>
            )}
            <label data-testid="editor-video-upload" className="flex w-fit cursor-pointer items-center gap-2 border border-white/15 px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-paper/70 transition-colors hover:border-acid hover:text-acid">
              <Upload className="h-3.5 w-3.5" />
              {uploading === "video" ? "Uploading… (large files take a minute)" : form.video ? "Replace Video" : "Upload Video"}
              <input type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={onPick("video")} />
            </label>
          </div>

          <div>
            <span className={labelCls}>Gallery Photos</span>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {form.gallery.map((g, i) => (
                <div key={g.src} className="group relative border border-white/10">
                  <img src={mediaUrl(g.src)} alt={g.alt} className="aspect-[4/3] w-full object-cover" />
                  <button
                    type="button"
                    data-testid={`editor-gallery-remove-${i}`}
                    onClick={() => setForm((f) => ({ ...f, gallery: f.gallery.filter((_, x) => x !== i) }))}
                    className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center bg-ink/80 text-paper/70 transition-colors hover:bg-red-500 hover:text-white"
                    aria-label={`Remove photo ${i + 1}`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <label data-testid="editor-gallery-upload" className="flex aspect-[4/3] cursor-pointer flex-col items-center justify-center gap-2 border border-dashed border-white/20 font-mono text-[10px] uppercase tracking-[0.2em] text-paper/50 transition-colors hover:border-acid hover:text-acid">
                <Upload className="h-4 w-4" />
                {uploading === "gallery" ? "Uploading…" : "Add Photos"}
                <input type="file" accept="image/*" multiple className="hidden" onChange={onPick("gallery")} />
              </label>
            </div>
          </div>
        </div>

        {status && (
          <p data-testid="editor-status" className="mt-6 font-mono text-[11px] uppercase tracking-[0.15em] text-red-400">
            {status}
          </p>
        )}

        <button
          type="button"
          data-testid="editor-save-button"
          onClick={save}
          disabled={saving || !!uploading || !form.title || !form.slug}
          className="mt-10 w-full bg-acid py-4 font-mono text-xs font-bold uppercase tracking-[0.25em] text-ink transition-colors hover:bg-paper disabled:opacity-40 sm:w-auto sm:px-12"
        >
          {saving ? "Saving…" : projectId ? "Save Changes" : "Publish Project"}
        </button>
      </main>
    </div>
  );
}
